<template>
    <UserPageLayout>
        <div style="margin-bottom: 4rem;">
            <h1 class="text-white text-center text-3xl">{{ $t('purchase_header_buy_credit') }}</h1>
            <p class="text-xl text-center text-white">{{ $t('purchase_buy_credit_description') }}</p>
        </div>

        <div class="max-w-3xl mx-auto mb-4">

            <div class="flex flex-wrap justify-center">
                <div :class="blockClass">
                    <div class="plan-block">
                        <p class="text-xl text-center shadow-text-none mt-2 mb-0">{{ $t('purchase_buy_credit_modal_header') }}</p>
                        <div class="flex w-full justify-center items-center p-2 plan-title">
                            <div>
                                <button @click="selectPrevPlan" :class="{
                                    'text-3xl payment-control-btn border-0': true,
                                    'text-red cursor-pointer': isFinalPlan,
                                    'text-grey cursor-pointer': !isFinalPlan,
                                    'cursor-not-allowed': isFirstPlan
                                }">
                                    <i class="fas fa-minus"></i>
                                </button>
                            </div>
                            <div class="text-4xl mx-3 font-bold">
                                {{ selectedPlan.credit }}
                            </div>
                            <div>
                                <button @click="selectNextPlan" :class="{
                                    'text-3xl payment-control-btn': true,
                                    'text-red cursor-pointer': !isFinalPlan,
                                    'text-grey cursor-not-allowed': isFinalPlan
                                }">
                                    <i class="fas fa-plus"></i>
                                </button>
                            </div>
                        </div>
                        <div class="text-3xl md:text-5xl mt-1 pb-1 mb-0 mx-0 text-center plan-title">
                            <h3 class="m-0 price-text text-4xl shadow-text-none" ref="priceText">${{ selectedPlan.price }}</h3>
                        </div>
                        <div class="py-3 px-4">
                            <ul class="plan-list list-reset text-center">
                                <li class="text-base">{{ $t('purchase_buy_credit_modal_description', { credit: selectedPlan.credit, price: selectedPlan.price, s: selectedPlan.credit > 1 ? 's' : '' }) }}</li>
                                <li class="text-base">{{ $t('purchase_buy_credit_modal_description2') }}</li>
                            </ul>
                            <button
                                type="button"
                                class="btn btn-outline btn-stripe w-full font-opensans font-size-15 mb-1"
                                style="padding: .7rem 0;"
                                @click="openStripeModal"
                            >
                                {{ $t('purchase_button_stripe') }}
                            </button>
                            <div class="paypal-button-wrapper">
                                <PaypalButton
                                    @success="paidSuccess"
                                    @creating="toggleLoading(true)"
                                    @cancel="toggleLoading(false)"
                                    @error="toggleLoading(false)"
                                    :price="selectedPlan.price"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <stripe-checkout
            :open="stripeOpen"
            :perUnit="`${selectedPlan.credit} credit(s)`"
            :paymentDescription="'Buy credit'"
            :amount="selectedPlan.price"
            @close="closeStripeModal"
            @result="paidByStripe"
        ></stripe-checkout>
    </UserPageLayout>
</template>

<script>

import Request from '@/lib/request'
import UserService from '@/lib/user'
import appConfig from '@/app.config'
import UserPageLayout from '@/layouts/UserPage'
import PaypalButton from '@/components/PaypalButton'
import stripeCheckout from '@/components/StripeCheckout'
import { currentUser } from '@/lib/user'
import popup from '@/lib/popup'
var CustomEvent = require('custom-event')

export default {

    components: {
        UserPageLayout,
        PaypalButton,
        stripeCheckout
    },

    data () {
        return {
            amount: 1,
            amountMin: 1,
            amountMax: 99,
            selectedPlanIndex: 0,
            stripeOpen: false
        }
    },

    mounted () {
        document.body.style.overflow = 'auto'
    },

    computed: {
        blockClass () {
            return 'w-full sm:w-full md:w-1/2 lg:w-1/3 xl:w-1/3 p-2'
        },

        plans () {
            return appConfig.buyCreditPlan
        },

        selectedPlan() {
            return this.plans[this.selectedPlanIndex]
        },

        isFinalPlan() {
            return this.selectedPlanIndex + 1 == this.plans.length
        },

        isFirstPlan() {
            return this.selectedPlanIndex == 0
        },
    },

    methods: {
        subscribe (e) {

            const userService = new UserService

            userService.validateToken()
                .then(() => {
                    this.$store.commit('toggleMainLoader', true)
                    e.target.submit()
                })
                .catch(() => {
                    const openAuthModalEvent = new CustomEvent('auth-show')
                    window.dispatchEvent(openAuthModalEvent)
                })
        },

        openStripeModal () {
            this.stripeOpen = true;
        },

        closeStripeModal () {
            this.stripeOpen = false;
        },

        paidByStripe (res) {
            if (res.status_code == 200) {
                const selected = this.plans[this.selectedPlanIndex]

                const payload = {
                    order_id: res.data.id,
                    payer_name: currentUser().name,
                    payer_email: currentUser().email,
                    payer_id: currentUser().id,
                    amount: selected.price,
                    credits: parseFloat(selected.credit).toFixed(2),
                    user_id: currentUser().id,
                    method: res.data.method,
                    type: 'buy-credit',
                    quiz_id: this.quizId
                }

                const request = new Request

                request.post(appConfig.api.saveBuyPlayerOrder, payload)
                    .then(() => {
                        this.$store.dispatch('getCredit')

                        this.closeStripeModal()
                        popup.alert(this.$t('purchase_alert_success'), function () {}, {icon: 'success'})
                    })
                    .catch(() => {
                        this.closeStripeModal()
                        popup.alert(this.$t('purchase_alert_fail'), function () {}, {icon: 'error'})
                    })
            } else {
                this.closeStripeModal()
                popup.alert(res.message, function () {}, {icon: 'error'})
            }
        },

        paidSuccess (details) {

            const selected = this.plans[this.selectedPlanIndex]

            const payload = {
                order_id: details.id,
                payer_name: `${details.payer.name.given_name} ${details.payer.name.surname}`,
                payer_email: details.payer.email_address,
                payer_id: details.payer.payer_id,
                amount: selected.price,
                credits: parseFloat(selected.credit).toFixed(2),
                user_id: currentUser().id,
                quiz_id: this.quizId,
                type: 'buy-credit',
                method: 'paypal'
            }

            const request = new Request

            request.post(appConfig.api.saveBuyPlayerOrder, payload)
                .then(() => {
                    this.$store.dispatch('getCredit')
                    this.toggleLoading(false)
                    this.$emit('close')
                    this.$emit('success', payload)
                    popup.alert(this.$t('purchase_alert_success'), function () {}, {icon: 'success'})
                })
                .catch(() => {
                    this.toggleLoading(false)
                    this.$emit('close')
                    popup.alert(this.$t('purchase_alert_fail'), function () {}, {icon: 'error'})
                })
        },

        animatePrice () {
            this.$refs.priceText.style.transform = 'scale(1.5)'
            setTimeout(() => {
                this.$refs.priceText.style.transform = 'scale(1)'
            }, 400)
        },

        selectNextPlan () {
            if ((this.selectedPlanIndex + 1) >= this.plans.length) {
                return
            }

            this.animatePrice()
            this.selectedPlanIndex++
        },

        selectPrevPlan () {
            if (this.selectedPlanIndex <= 0) {
                return
            }

            this.animatePrice()
            this.selectedPlanIndex--
        },

        toggleLoading (val) {
            this.$store.commit('toggleMainLoader', val)
        }
    }
}
</script>

<style lang="scss">
$input-border-color: #cbcbcb;
$input-border-darker-color: #b6b6b6;

.plan-block {
    border: 1px solid $input-border-color;
    background-color: #fff;
    border-radius: 5px;
    // padding: 1rem;
    transition: all .3s ease;

    .plan-price {
        font-size: 2.3rem;
        margin: 0 0 .5rem 0;
    }

    &:hover {
        // transform: scale(1.03);
        // box-shadow: 0px 0px 20px rgba(0, 0, 0, 0.4);
    }

    .price-text {
        transition: all .3s ease;
    }

    .plan-title {
        border-bottom: 1px solid $input-border-darker-color;
    }

    .payment-control-btn {
        background-color: transparent;
        border: none;
        padding: .5rem 1rem;
        border-radius: 5px;
    }

    .become-contributor-content {
        height: 9.9rem;

        @media screen and (max-width: 1107px) {
            height: 6.6rem;
        }

        @media screen and (max-width: 991px) {
            height: 10rem;
        }

        @media screen and (max-width: 748px) {
            height: 7rem;
        }
    }

    .become-contributor-btn {

        @media screen and (max-width: 658px) {
            font-size: .9rem;
        }
    }

    .btn-stripe {
        background-color: #6772E5 !important;
        border: 1px solid transparent;
        border-radius: 4px;
        box-shadow: none;
        padding-left: 0;
        padding-right: 0;
    }

    .btn-stripe:hover {
        opacity: .8;
    }
}
</style>
